import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShoppingBag, Eye, X, RefreshCw, User, Clock,
  ChevronLeft, ChevronRight, MapPin, AlertTriangle, Check, Ban,
} from 'lucide-react';
import { api } from '../api/client';
import { useToast } from '../components/Toast';
import { ConfirmModal } from '../components/ConfirmModal';

// ─── Types ───────────────────────────────────────────────────────────────────

type Channel = 'whatsapp' | 'online';
type FulfillmentStatus = 'preparando' | 'enviado' | 'entregado';

interface UnifiedOrderRow {
  id: string;
  channel: Channel;
  customerName: string;
  total: number;
  itemCount: number;
  deliveryType: 'envio' | 'retiro';
  paymentStatus: string | null;
  fulfillmentStatus: FulfillmentStatus;
  createdAt: number;
}

interface WhatsAppLeadItem {
  productId: string;
  name: string;
  size: string;
  quantity: number;
  price: number;
}

interface WhatsAppLeadDetail {
  id: string;
  customerName: string;
  items: string; // JSON string
  totalAmount: number;
  itemCount: number;
  deliveryType: 'envio' | 'retiro';
  fulfillmentStatus: FulfillmentStatus;
  createdAt: number;
}

interface OrderItemDetail {
  id: string;
  productName: string;
  size: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

interface PaymentDetail {
  id: string;
  status: string;
  amount: number;
}

interface OrderDetail {
  id: string;
  customerId: string;
  customer: { name: string; email: string; phone: string };
  deliveryType: 'envio' | 'retiro';
  shippingAddress: string | null;
  shippingLocality: string | null;
  shippingProvince: string | null;
  shippingPostalCode: string | null;
  subtotal: number;
  shippingCost: number;
  total: number;
  paymentStatus: string;
  fulfillmentStatus: FulfillmentStatus;
  createdAt: number;
  items: OrderItemDetail[];
  payments: PaymentDetail[];
}

type SelectedDetail =
  | { channel: 'whatsapp'; lead: WhatsAppLeadDetail }
  | { channel: 'online'; order: OrderDetail; receivedAmount: number | null };

const LIMIT = 20;

const FULFILLMENT_LABELS: Record<FulfillmentStatus, string> = {
  preparando: 'Preparando',
  enviado: 'Enviado',
  entregado: 'Entregado',
};

function fmtPrice(n: number) {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
}

function fmtDate(unix: number): string {
  return new Date(unix * 1000).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getParsedItems(jsonStr: string): WhatsAppLeadItem[] {
  try {
    return JSON.parse(jsonStr) as WhatsAppLeadItem[];
  } catch {
    return [];
  }
}

export const Orders: React.FC = () => {
  const toast = useToast();

  const [items, setItems] = useState<UnifiedOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [channelFilter, setChannelFilter] = useState<'' | Channel>('');
  const [fulfillmentFilter, setFulfillmentFilter] = useState<'' | FulfillmentStatus>('');
  const [reviewOnly, setReviewOnly] = useState(false);

  const [selected, setSelected] = useState<SelectedDetail | null>(null);
  const [resolveAction, setResolveAction] = useState<'approve' | 'reject' | null>(null);
  const [resolving, setResolving] = useState(false);
  const [loadingDetailKey, setLoadingDetailKey] = useState<string | null>(null);
  const fetchSeqRef = useRef(0);

  const fetchOrders = useCallback(async (page: number) => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (channelFilter) params.set('channel', channelFilter);
      if (fulfillmentFilter) params.set('fulfillmentStatus', fulfillmentFilter);
      // reviewOnly pide el filtro server-side (paymentStatus=review) en vez de filtrar
      // client-side la página ya traída — si las órdenes en revisión están en otra
      // página, un filtro client-side las dejaría afuera incorrectamente.
      if (reviewOnly) params.set('paymentStatus', 'review');
      const res = await api.get<{ items: UnifiedOrderRow[]; total: number; reviewCount: number }>(
        `/api/v1/admin/orders?${params.toString()}`
      );
      if (seq !== fetchSeqRef.current) return; // una fetch más nueva ya se disparó — descartar esta respuesta
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setReviewCount(res.data.reviewCount || 0);
      if (reviewOnly && (res.data.reviewCount || 0) === 0) {
        setReviewOnly(false);
      }
    } catch (e) {
      if (seq === fetchSeqRef.current) {
        toast.error({ title: 'Error', description: e instanceof Error ? e.message : 'No se pudieron cargar los pedidos.' });
      }
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelFilter, fulfillmentFilter, reviewOnly]);

  useEffect(() => {
    fetchOrders(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, channelFilter, fulfillmentFilter, reviewOnly]);

  const totalPages = Math.ceil(total / LIMIT);

  const openDetail = async (row: UnifiedOrderRow) => {
    const rowKey = `${row.channel}-${row.id}`;
    setLoadingDetailKey(rowKey);
    try {
      const res = await api.get<SelectedDetail>(`/api/v1/admin/orders/${row.channel}/${row.id}`);
      setSelected(res.data);
    } catch (e) {
      toast.error({ title: 'Error', description: e instanceof Error ? e.message : 'No se pudo cargar el detalle.' });
    } finally {
      setLoadingDetailKey(null);
    }
  };

  const changeFulfillment = async (row: UnifiedOrderRow, next: FulfillmentStatus) => {
    const seq = fetchSeqRef.current;
    const previous = row.fulfillmentStatus;
    setItems(prev => prev.map(i => (i.id === row.id && i.channel === row.channel ? { ...i, fulfillmentStatus: next } : i)));
    try {
      await api.patch(`/api/v1/admin/orders/${row.channel}/${row.id}/fulfillment`, { fulfillmentStatus: next });
      toast.success({ title: 'Estado actualizado', description: `Pedido marcado como "${FULFILLMENT_LABELS[next]}".` });
    } catch (e) {
      // Si mientras tanto se disparó un fetch nuevo (ej. el admin cambió de filtro), la
      // lista ya fue reemplazada por completo — revertir acá pisaría datos de otro filtro.
      if (seq === fetchSeqRef.current) {
        setItems(prev => prev.map(i => (i.id === row.id && i.channel === row.channel ? { ...i, fulfillmentStatus: previous } : i)));
      }
      toast.error({ title: 'Error', description: e instanceof Error ? e.message : 'No se pudo actualizar el estado.' });
    }
  };

  const resolveReview = async () => {
    if (!selected || selected.channel !== 'online' || !resolveAction) return;
    setResolving(true);
    try {
      await api.post(`/api/v1/admin/orders/${selected.order.id}/resolve-review`, { action: resolveAction });
      toast.success({
        title: resolveAction === 'approve' ? 'Orden aprobada' : 'Orden rechazada',
        description: resolveAction === 'approve'
          ? 'Se marcó como pagada y se descontó el stock.'
          : 'Se marcó como rechazada, sin tocar stock.',
      });
      setResolveAction(null);
      setSelected(null);
      fetchOrders(currentPage);
    } catch (e) {
      toast.error({ title: 'Error', description: e instanceof Error ? e.message : 'No se pudo resolver la orden.' });
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between md:items-center bg-page-surface px-5 py-3 rounded-2xl border border-page-border gap-4 shadow-sm">
        <div>
          <h2 className="font-heading font-bold text-lg text-ink leading-tight flex items-center gap-2">
            <span>Pedidos</span>
            <span className="text-xs text-ink-faint font-semibold bg-white border border-page-border px-2 py-0.5 rounded-full">
              {total}
            </span>
          </h2>
          <p className="text-xs text-ink-muted mt-0.5">Pedidos enviados por WhatsApp y ventas pagadas online.</p>
        </div>
        <button
          onClick={() => fetchOrders(currentPage)}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-page-border rounded-xl text-xs font-semibold text-ink-muted hover:text-ink hover:border-brand-border transition-colors self-start md:self-auto shadow-sm"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Actualizar</span>
        </button>
      </div>

      {/* Banner de revisión */}
      {reviewCount > 0 && (
        <button
          onClick={() => { setReviewOnly(v => !v); setCurrentPage(1); }}
          className={`w-full flex items-center gap-2.5 px-4 py-3 rounded-2xl border text-left transition-colors ${
            reviewOnly
              ? 'bg-amber-100 border-amber-300 text-amber-800'
              : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
          }`}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs font-semibold">
            {reviewCount} pedido{reviewCount === 1 ? '' : 's'} necesita{reviewCount === 1 ? '' : 'n'} revisión
            {reviewOnly ? ' — mostrando solo estos' : ' — click para ver'}
          </span>
        </button>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 bg-page-surface border border-page-border rounded-2xl px-4 py-3 shadow-sm">
        <select
          value={channelFilter}
          onChange={e => { setChannelFilter(e.target.value as '' | Channel); setCurrentPage(1); }}
          className="text-xs border border-page-border bg-white rounded-xl px-3 py-1.5 text-ink focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-colors cursor-pointer"
        >
          <option value="">Todos los canales</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="online">Online</option>
        </select>

        <select
          value={fulfillmentFilter}
          onChange={e => { setFulfillmentFilter(e.target.value as '' | FulfillmentStatus); setCurrentPage(1); }}
          className="text-xs border border-page-border bg-white rounded-xl px-3 py-1.5 text-ink focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-colors cursor-pointer"
        >
          <option value="">Todos los estados</option>
          <option value="preparando">Preparando</option>
          <option value="enviado">Enviado</option>
          <option value="entregado">Entregado</option>
        </select>
      </div>

      {/* Grid / Table */}
      {loading && items.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <LoaderSpinner />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-ink-faint bg-page-surface rounded-2xl border border-page-border">
          <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-ink-faint" />
          <p className="text-sm">
            {channelFilter || fulfillmentFilter || reviewOnly
              ? 'No hay pedidos que coincidan con los filtros aplicados.'
              : 'No se registraron pedidos aún.'}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile view */}
          <div className="md:hidden space-y-3">
            {items.map((row) => (
              <div
                key={`${row.channel}-${row.id}`}
                className={`bg-white border rounded-2xl p-4 space-y-3 shadow-sm ${
                  row.paymentStatus === 'review' ? 'border-amber-300 bg-amber-50/40' : 'border-page-border'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-ink text-sm flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-ink-faint" />
                      {row.customerName}
                    </h4>
                    <p className="text-[10px] text-ink-faint mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {fmtDate(row.createdAt)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <ChannelBadge channel={row.channel} />
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                      row.deliveryType === 'envio'
                        ? 'bg-blue-50 border-blue-200 text-blue-600'
                        : 'bg-amber-50 border-amber-200 text-amber-600'
                    }`}>
                      {row.deliveryType === 'envio' ? 'Envío' : 'Retiro'}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-page-border/50">
                  <div className="text-xs">
                    <span className="text-ink-muted">Pares:</span> <span className="font-bold text-ink">{row.itemCount}</span>
                  </div>
                  <div className="text-sm font-bold text-ink">
                    {fmtPrice(row.total)}
                  </div>
                </div>

                <FulfillmentSelect value={row.fulfillmentStatus} onChange={next => changeFulfillment(row, next)} />

                <button
                  onClick={() => openDetail(row)}
                  disabled={loadingDetailKey === `${row.channel}-${row.id}`}
                  className="w-full py-2 bg-brand-surface border border-brand-border text-brand-primary rounded-xl transition-all flex items-center justify-center gap-1.5 text-xs font-bold disabled:opacity-50"
                >
                  {loadingDetailKey === `${row.channel}-${row.id}` ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Eye className="w-3.5 h-3.5" />
                  )}
                  <span>Ver Detalle</span>
                </button>
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto bg-white rounded-2xl border border-page-border shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-page-border text-ink-muted text-xs font-semibold uppercase tracking-wider bg-page-surface">
                  <th className="px-6 py-4">Cliente</th>
                  <th className="px-6 py-4">Fecha</th>
                  <th className="px-6 py-4">Canal</th>
                  <th className="px-6 py-4">Método</th>
                  <th className="px-6 py-4 text-center">Pares</th>
                  <th className="px-6 py-4">Total</th>
                  <th className="px-6 py-4">Cumplimiento</th>
                  <th className="px-6 py-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-page-border text-sm">
                {items.map((row) => (
                  <tr
                    key={`${row.channel}-${row.id}`}
                    className={`hover:bg-page-surface transition-colors ${row.paymentStatus === 'review' ? 'bg-amber-50/60' : ''}`}
                  >
                    <td className="px-6 py-4 font-semibold text-ink">{row.customerName}</td>
                    <td className="px-6 py-4 text-ink-muted text-xs">{fmtDate(row.createdAt)}</td>
                    <td className="px-6 py-4"><ChannelBadge channel={row.channel} /></td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wide ${
                        row.deliveryType === 'envio'
                          ? 'bg-blue-50 border-blue-200 text-blue-600'
                          : 'bg-amber-50 border-amber-200 text-amber-600'
                      }`}>
                        {row.deliveryType === 'envio' ? 'Envío' : 'Retiro'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center font-bold text-ink">{row.itemCount}</td>
                    <td className="px-6 py-4 font-bold text-brand-primary">{fmtPrice(row.total)}</td>
                    <td className="px-6 py-4">
                      <FulfillmentSelect value={row.fulfillmentStatus} onChange={next => changeFulfillment(row, next)} />
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => openDetail(row)}
                        disabled={loadingDetailKey === `${row.channel}-${row.id}`}
                        className="p-2 text-ink-faint hover:text-brand-primary hover:bg-brand-surface rounded-lg transition-colors border border-transparent hover:border-brand-border disabled:opacity-50"
                        title="Ver detalle"
                      >
                        {loadingDetailKey === `${row.channel}-${row.id}` ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-xs text-ink-faint">
                Pág. {currentPage} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                  className="p-1.5 border border-page-border hover:border-brand-border disabled:opacity-40 rounded-lg bg-white transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-ink-muted" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || loading}
                  className="p-1.5 border border-page-border hover:border-brand-border disabled:opacity-40 rounded-lg bg-white transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-ink-muted" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-[9999] flex justify-center items-center p-4">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setSelected(null)} />

          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-page-border animate-fade-scale z-10 overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-page-border flex justify-between items-center bg-page-surface">
              <div>
                <h3 className="font-heading font-bold text-lg text-ink">Detalle del Pedido</h3>
                <p className="text-[10px] text-ink-faint mt-0.5">
                  ID: {selected.channel === 'whatsapp' ? selected.lead.id : selected.order.id}
                </p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-ink-faint hover:text-ink p-1.5 hover:bg-page-panel rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {selected.channel === 'whatsapp' ? (
                <>
                  <div className="grid grid-cols-2 gap-4 bg-page-surface p-4 rounded-xl border border-page-border/60">
                    <div>
                      <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Cliente</span>
                      <span className="text-sm font-semibold text-ink flex items-center gap-1.5 mt-0.5">
                        <User className="w-3.5 h-3.5 text-ink-faint" />
                        {selected.lead.customerName}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Método</span>
                      <span className="text-sm font-semibold text-ink flex items-center gap-1.5 mt-0.5">
                        <MapPin className="w-3.5 h-3.5 text-ink-faint" />
                        {selected.lead.deliveryType === 'envio' ? 'Envío a domicilio' : 'Retiro por local'}
                      </span>
                    </div>
                    <div className="col-span-2 border-t border-page-border/40 pt-2.5">
                      <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Fecha de Creación</span>
                      <span className="text-xs font-semibold text-ink flex items-center gap-1.5 mt-0.5">
                        <Clock className="w-3.5 h-3.5 text-ink-faint" />
                        {fmtDate(selected.lead.createdAt)}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    <span className="block text-[10px] font-bold text-ink-muted uppercase tracking-wider">Productos solicitados</span>
                    <div className="divide-y divide-page-border border border-page-border rounded-xl overflow-hidden bg-white">
                      {getParsedItems(selected.lead.items).map((p, idx) => (
                        <div key={`${p.productId}-${idx}`} className="flex justify-between items-center p-3 hover:bg-page-surface transition-colors">
                          <div>
                            <h4 className="font-semibold text-ink text-xs">{p.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] font-bold text-ink-muted bg-page-surface border border-page-border px-1 py-0.5 rounded">
                                Talle: {p.size}
                              </span>
                              <span className="text-[10px] text-ink-faint">
                                Cant: {p.quantity} x {fmtPrice(p.price)}
                              </span>
                            </div>
                          </div>
                          <div className="font-bold text-xs text-ink">
                            {fmtPrice(p.price * p.quantity)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-page-border pt-4 flex justify-between items-center">
                    <span className="text-sm font-semibold text-ink-muted">Total del Pedido</span>
                    <span className="text-xl font-bold text-brand-primary">{fmtPrice(selected.lead.totalAmount)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 bg-page-surface p-4 rounded-xl border border-page-border/60">
                    <div>
                      <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Cliente</span>
                      <span className="text-sm font-semibold text-ink flex items-center gap-1.5 mt-0.5">
                        <User className="w-3.5 h-3.5 text-ink-faint" />
                        {selected.order.customer.name}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Método</span>
                      <span className="text-sm font-semibold text-ink flex items-center gap-1.5 mt-0.5">
                        <MapPin className="w-3.5 h-3.5 text-ink-faint" />
                        {selected.order.deliveryType === 'envio' ? 'Envío a domicilio' : 'Retiro por local'}
                      </span>
                    </div>
                    {selected.order.deliveryType === 'envio' && (
                      <div className="col-span-2 border-t border-page-border/40 pt-2.5">
                        <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Dirección de envío</span>
                        <span className="text-xs font-semibold text-ink mt-0.5 block">
                          {selected.order.shippingAddress}, {selected.order.shippingLocality}, {selected.order.shippingProvince} ({selected.order.shippingPostalCode})
                        </span>
                      </div>
                    )}
                    <div className="col-span-2 border-t border-page-border/40 pt-2.5">
                      <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Fecha de Creación</span>
                      <span className="text-xs font-semibold text-ink flex items-center gap-1.5 mt-0.5">
                        <Clock className="w-3.5 h-3.5 text-ink-faint" />
                        {fmtDate(selected.order.createdAt)}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    <span className="block text-[10px] font-bold text-ink-muted uppercase tracking-wider">Productos</span>
                    <div className="divide-y divide-page-border border border-page-border rounded-xl overflow-hidden bg-white">
                      {selected.order.items.map((item) => (
                        <div key={item.id} className="flex justify-between items-center p-3 hover:bg-page-surface transition-colors">
                          <div>
                            <h4 className="font-semibold text-ink text-xs">{item.productName}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] font-bold text-ink-muted bg-page-surface border border-page-border px-1 py-0.5 rounded">
                                Talle: {item.size}
                              </span>
                              <span className="text-[10px] text-ink-faint">
                                Cant: {item.quantity} x {fmtPrice(item.unitPrice)}
                              </span>
                            </div>
                          </div>
                          <div className="font-bold text-xs text-ink">{fmtPrice(item.subtotal)}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-page-border pt-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-ink-muted">Estado de pago</span>
                      <PaymentStatusBadge status={selected.order.paymentStatus} />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-ink-muted">Total del Pedido</span>
                      <span className="text-xl font-bold text-brand-primary">{fmtPrice(selected.order.total)}</span>
                    </div>
                  </div>

                  {selected.order.paymentStatus === 'review' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2 text-amber-800">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-xs font-bold">Requiere revisión manual</span>
                      </div>
                      <div className="text-xs text-amber-700 space-y-1">
                        <p>Monto esperado: <strong>{fmtPrice(selected.order.payments[0]?.amount ?? selected.order.total)}</strong></p>
                        <p>
                          Monto recibido de Mercado Pago:{' '}
                          <strong>{selected.receivedAmount !== null ? fmtPrice(selected.receivedAmount) : 'No disponible'}</strong>
                        </p>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={() => setResolveAction('approve')}
                          className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
                        >
                          <Check className="w-3.5 h-3.5" /> Aprobar
                        </button>
                        <button
                          onClick={() => setResolveAction('reject')}
                          className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
                        >
                          <Ban className="w-3.5 h-3.5" /> Rechazar
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-4 bg-page-surface border-t border-page-border flex justify-end">
              <button
                onClick={() => setSelected(null)}
                className="px-4 py-2 bg-white border border-page-border text-ink-muted hover:text-ink hover:border-brand-border rounded-xl text-xs font-bold shadow-sm transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={resolveAction !== null}
        onClose={() => (resolving ? undefined : setResolveAction(null))}
        onConfirm={resolveReview}
        loading={resolving}
        type={resolveAction === 'approve' ? 'info' : 'danger'}
        title={resolveAction === 'approve' ? 'Aprobar esta orden' : 'Rechazar esta orden'}
        message={
          resolveAction === 'approve'
            ? 'Se va a marcar la orden como pagada y se va a descontar el stock vendido. Esta acción no se puede deshacer.'
            : 'Se va a marcar la orden como rechazada, sin tocar el stock. Esta acción no se puede deshacer.'
        }
        confirmText={resolveAction === 'approve' ? 'Sí, aprobar' : 'Sí, rechazar'}
      />
    </div>
  );
};

const ChannelBadge: React.FC<{ channel: Channel }> = ({ channel }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wide ${
    channel === 'online'
      ? 'bg-violet-50 border-violet-200 text-violet-600'
      : 'bg-emerald-50 border-emerald-200 text-emerald-600'
  }`}>
    {channel === 'online' ? 'Online' : 'WhatsApp'}
  </span>
);

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  rejected: 'Rechazado',
  cancelled: 'Cancelado',
  review: 'En revisión',
};

const PaymentStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wide ${
    status === 'paid'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
      : status === 'review'
      ? 'bg-amber-50 border-amber-200 text-amber-700'
      : status === 'pending'
      ? 'bg-slate-50 border-slate-200 text-slate-600'
      : 'bg-rose-50 border-rose-200 text-rose-600'
  }`}>
    {PAYMENT_STATUS_LABELS[status] ?? status}
  </span>
);

const FulfillmentSelect: React.FC<{ value: FulfillmentStatus; onChange: (next: FulfillmentStatus) => void }> = ({ value, onChange }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value as FulfillmentStatus)}
    onClick={e => e.stopPropagation()}
    className="text-[11px] font-semibold border border-page-border bg-white rounded-lg px-2 py-1 text-ink focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-colors cursor-pointer"
  >
    <option value="preparando">Preparando</option>
    <option value="enviado">Enviado</option>
    <option value="entregado">Entregado</option>
  </select>
);

const LoaderSpinner: React.FC = () => (
  <div className="flex items-center gap-2 text-ink-muted text-xs">
    <RefreshCw className="w-4 h-4 animate-spin text-brand-primary" />
    <span>Cargando pedidos...</span>
  </div>
);
